import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactDOM from 'react-dom';
import { FaTrash, FaArrowLeft, FaTimes } from 'react-icons/fa';
import { cleanupAuditLogs } from '../../../redux/Slices/AuditLogSlice';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';

const CleanupAuditLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [olderThanDays, setOlderThanDays] = useState(90);
  const [isCleaning, setIsCleaning] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const getUserData = () => {
    if (user) return user;
    const storedUser = localStorage.getItem("useralbaraqawy");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  };

  const userData = getUserData();
  const getUserPermissions = () => {
    if (Array.isArray(userData?.data?.permissions)) return userData.data.permissions;
    if (Array.isArray(userData?.permissions)) return userData.permissions;
    if (Array.isArray(userData?.data?.data?.permissions)) return userData.data.data.permissions;
    return [];
  };

  const getUserRoles = () => {
    if (Array.isArray(userData?.data?.roles)) return userData.data.roles;
    if (Array.isArray(userData?.roles)) return userData.roles;
    if (Array.isArray(userData?.data?.data?.roles)) return userData.data.data.roles;
    return [];
  };

  const userPermissions = getUserPermissions();
  const userRoles = getUserRoles();
  
  // Check if user has cleanup permission or is admin
  const hasCleanupPermission = () => {
    const isAdmin = userRoles.some(role => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return roleName === 'admin' || roleName === 'Admin' || roleName === 'administrator' || roleName === 'Administrator';
    });
    if (isAdmin) return true;
    return userPermissions.includes('audit_logs.cleanup');
  };

  const canCleanup = hasCleanupPermission();

  useEffect(() => {
    setTitle('auditLogs.cleanupTitle', { defaultValue: 'تنظيف سجلات التحركات' });
    
    // Check if user has cleanup permission
    if (!canCleanup) {
      toast.error(t('auditLogs.errors.unauthorized', { defaultValue: 'غير مصرح لك بالوصول إلى هذه الصفحة' }), { rtl: isRTL });
      navigate('/audit-logs');
    }
  }, [setTitle, canCleanup, navigate, t, isRTL]);

  const handleCleanupClick = () => {
    if (!canCleanup) {
      toast.error(t('auditLogs.errors.unauthorized', { defaultValue: 'غير مصرح لك بالوصول إلى هذه الصفحة' }), { rtl: isRTL });
      return;
    }

    if (!olderThanDays || olderThanDays < 1) {
      toast.error(t('auditLogs.errors.invalidDays', { defaultValue: 'يرجى إدخال عدد أيام صحيح' }), { rtl: isRTL });
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmCleanup = async () => {
    setIsCleaning(true);
    setShowConfirmModal(false);
    try {
      const result = await dispatch(cleanupAuditLogs({ olderThanDays, language: i18n.language }));
      if (cleanupAuditLogs.fulfilled.match(result)) {
        toast.success(
          t('auditLogs.success.cleaned', { 
            defaultValue: 'تم حذف السجلات بنجاح',
            count: result.payload?.deleted_count || 0
          }), 
          { rtl: isRTL }
        );
        navigate('/audit-logs');
      } else {
        toast.error(result.payload || t('auditLogs.errors.cleanupError', { defaultValue: 'فشل في حذف السجلات' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.cleanupError', { defaultValue: 'فشل في حذف السجلات' }), { rtl: isRTL });
    } finally {
      setIsCleaning(false);
    }
  };

  if (!canCleanup) {
    return null;
  }

  return (
    <div style={{ padding: '20px', direction: dir, textAlign }}>
      <div style={{ 
        backgroundColor: '#1b3350', 
        borderRadius: '12px', 
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            {t('auditLogs.cleanupTitle', { defaultValue: 'تنظيف سجلات التحركات' })}
          </h2>
          <button
            onClick={() => navigate('/audit-logs')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowLeft /> {t('auditLogs.back', { defaultValue: 'رجوع' })}
          </button>
        </div>

        <div style={{ 
          backgroundColor: '#1a1f2e', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              {t('auditLogs.olderThanDays', { defaultValue: 'حذف السجلات الأقدم من (بالأيام)' })}
            </label>
            <input
              type="number"
              min="1"
              value={olderThanDays}
              onChange={(e) => setOlderThanDays(parseInt(e.target.value) || 90)}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '10px',
                backgroundColor: '#14263b',
                border: '1px solid #333',
                borderRadius: '6px',
                color: 'white',
                fontSize: '16px'
              }}
            />
            <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
              {t('auditLogs.cleanupWarning', { 
                defaultValue: 'سيتم حذف جميع السجلات الأقدم من العدد المحدد من الأيام. هذا الإجراء لا يمكن التراجع عنه.',
                days: olderThanDays 
              })}
            </p>
          </div>

          <button
            onClick={handleCleanupClick}
            disabled={isCleaning}
            style={{
              backgroundColor: isCleaning ? '#666' : '#dc3545',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isCleaning ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '16px'
            }}
          >
            <FaTrash /> {isCleaning ? t('auditLogs.cleaning', { defaultValue: 'جاري الحذف...' }) : t('auditLogs.cleanup', { defaultValue: 'حذف السجلات' })}
          </button>
        </div>
      </div>

      {/* Confirm Cleanup Modal */}
      {showConfirmModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px',
              border: '1px solid #333',
              direction: dir,
              textAlign
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px' }}>
                {t('auditLogs.confirmCleanupTitle', { defaultValue: 'تأكيد حذف السجلات' })}
              </h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#ccc',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>

            <p style={{ color: '#ccc', marginBottom: '20px', lineHeight: 1.6 }}>
              {t('auditLogs.confirmCleanupMessage', { 
                defaultValue: `هل أنت متأكد من حذف جميع السجلات الأقدم من ${olderThanDays} يوم؟`,
                days: olderThanDays 
              })}
            </p>
            <p style={{ color: '#ff6b6b', marginBottom: '20px', fontSize: '14px', fontWeight: 'bold' }}>
              {t('auditLogs.irreversibleAction', { defaultValue: 'هذا الإجراء لا يمكن التراجع عنه.' })}
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('auditLogs.cancel', { defaultValue: 'إلغاء' })}
              </button>
              <button
                onClick={handleConfirmCleanup}
                disabled={isCleaning}
                style={{
                  backgroundColor: isCleaning ? '#666' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: isCleaning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaTrash /> {t('auditLogs.confirm', { defaultValue: 'تأكيد الحذف' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CleanupAuditLogs;
